import { mapProvisionToRecipient } from '@/modules/dispositiveProvisions/dispositiveProvisions.utils';
import { DispositiveProvisionsFormRecipient } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import {
  buildSequentialProvisionInputs,
  isRecipientWithId,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.utils';
import { calculateResiduaryRecipients } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/formBody/primaryClientDeathSection/primaryClientDeathSection.utils';
import { DispositiveProvisions_BeneficiaryDesignationGroupFragment } from '@/modules/dispositiveProvisions/graphql/DispositiveProvisions.fragments.generated';
import {
  AugmentedCreateBeneficiaryDesignationGroupInput,
  AugmentedUpdateBeneficiaryDesignationGroupInput,
  BeneficiaryDesignationTier,
} from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import {
  BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE,
  BeneficiaryDesignationsFormShape,
  BeneficiaryDesignationTierFormValues,
} from './BeneficiaryDesignationsForm.types';

/**
 * Builds the initial form values for the beneficiary designations editor
 * from the existing BDG data.
 *
 * `isInEstate` seeds the per-tier `residuaryPaysAllTaxes` default for tiers that
 * have no persisted flag yet, mirroring the DP form's
 * `defaultResiduaryPaysAllTaxes = isInEstate`.
 */
export function getInitialFormValues(
  bdgs: DispositiveProvisions_BeneficiaryDesignationGroupFragment[],
  isInEstate: boolean
): BeneficiaryDesignationsFormShape {
  const primaryBdg = bdgs.find(
    (b) => b.tier === BeneficiaryDesignationTier.Primary
  );
  const contingentBdg = bdgs.find(
    (b) => b.tier === BeneficiaryDesignationTier.Contingent
  );

  return {
    [BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE]: {
      primary: mapBdgToTierFormValues(
        primaryBdg,
        BeneficiaryDesignationTier.Primary,
        isInEstate
      ),
      contingent: mapBdgToTierFormValues(
        contingentBdg,
        BeneficiaryDesignationTier.Contingent,
        isInEstate
      ),
    },
  };
}

function mapBdgToTierFormValues(
  bdg: DispositiveProvisions_BeneficiaryDesignationGroupFragment | undefined,
  tier: BeneficiaryDesignationTier,
  isInEstate: boolean
): BeneficiaryDesignationTierFormValues {
  const defaultResiduaryPaysAllTaxes = isInEstate;

  if (!bdg) {
    return {
      beneficiaryDesignationGroupId: null,
      tier,
      recipients: [],
      residuaryPaysAllTaxes: defaultResiduaryPaysAllTaxes,
      _associatedHypotheticalWaterfallId: null,
    };
  }

  const provisions = getNodes(bdg.dispositiveProvisions);

  return {
    beneficiaryDesignationGroupId: bdg.id,
    tier,
    recipients: provisions.map(mapProvisionToRecipient),
    // Live-converted (un-migrated) groups are synthetic and carry an empty id;
    // their residuaryPaysAllTaxes is the resolver's zero value (false), not a
    // user choice, so fall back to the in-estate default for them. Real
    // persisted groups (non-empty id) use their saved flag.
    residuaryPaysAllTaxes: bdg.id
      ? bdg.residuaryPaysAllTaxes
      : defaultResiduaryPaysAllTaxes,
    _associatedHypotheticalWaterfallId:
      bdg.associatedHypotheticalWaterfall?.id ?? null,
  };
}

/**
 * When the tier's residuary pays all taxes, the residuary recipient(s) bear tax
 * and everyone else does not. The per-recipient "net of taxes" checkbox is
 * hidden in that mode, and the toggle hook only materializes `bearsTax` on a
 * flag flip — so recipients added/reordered while the flag is already on aren't
 * otherwise re-materialized. Re-derive `bearsTax` from the final recipient set
 * at submit, mirroring the DP form's `convertDPFormV2ScenarioToDPFormV1Scenario`.
 * When the flag is off, the per-recipient `bearsTax` values stand as-is.
 */
function reconcileResiduaryBearsTax(
  tierValues: BeneficiaryDesignationTierFormValues
): DispositiveProvisionsFormRecipient[] {
  if (!tierValues.residuaryPaysAllTaxes) {
    return tierValues.recipients;
  }

  const { residuaryIndices } = calculateResiduaryRecipients(
    tierValues.recipients
  );
  return tierValues.recipients.map((recipient, index) => ({
    ...recipient,
    bearsTax: residuaryIndices.includes(index),
  }));
}

interface MakeBdgInputOpts {
  householdId: string;
  isOnHypotheticalWaterfall: boolean;
  waterfallId?: string;
  entityId: string;
  entityType: 'retirement-account' | 'insurance-account';
  entitySubtypeId: string;
}

type BdgMutationInput =
  | { type: 'create'; input: AugmentedCreateBeneficiaryDesignationGroupInput }
  | { type: 'update'; input: AugmentedUpdateBeneficiaryDesignationGroupInput }
  | { type: 'noop' };

/**
 * Given the current and initial form values for a single tier, determines
 * whether to create, update, or no-op a BDG mutation.
 */
export function makeBdgMutationInputForTier(
  tierValues: BeneficiaryDesignationTierFormValues,
  initialTierValues: BeneficiaryDesignationTierFormValues,
  opts: MakeBdgInputOpts
): BdgMutationInput {
  const hasRecipients = tierValues.recipients.some(isRecipientWithId);
  // Only treat the initial BDG as "existing for this scope" if it matches the
  // scope we're saving to. extractBdgData falls back to live BDGs when a
  // hypothetical scope has no BDGs yet, so without this check we'd UPDATE the
  // live BDG (clobbering its provisions) when the user is actually trying to
  // create a new hypothetical BDG.
  const initialBdgMatchesScope = opts.isOnHypotheticalWaterfall
    ? initialTierValues._associatedHypotheticalWaterfallId === opts.waterfallId
    : initialTierValues._associatedHypotheticalWaterfallId === null;
  const hadExistingBdg =
    !!initialTierValues.beneficiaryDesignationGroupId && initialBdgMatchesScope;

  // No recipients and no existing BDG → nothing to do
  if (!hasRecipients && !hadExistingBdg) {
    return { type: 'noop' };
  }

  const withDispositiveProvisions = buildSequentialProvisionInputs(
    reconcileResiduaryBearsTax(tierValues),
    opts,
    opts.householdId
  );

  if (hadExistingBdg) {
    // Update existing BDG: clear old provisions, create new ones
    return {
      type: 'update',
      input: {
        id: initialTierValues.beneficiaryDesignationGroupId!,
        update: {
          clearDispositiveProvisions: true,
          residuaryPaysAllTaxes: tierValues.residuaryPaysAllTaxes,
        },
        withDispositiveProvisions,
      },
    };
  }

  // Create new BDG
  const accountIdField = getAccountIdField(opts);
  return {
    type: 'create',
    input: {
      create: {
        householdID: opts.householdId,
        tier: tierValues.tier,
        residuaryPaysAllTaxes: tierValues.residuaryPaysAllTaxes,
        ...accountIdField,
        associatedHypotheticalWaterfallID: opts.isOnHypotheticalWaterfall
          ? opts.waterfallId
          : undefined,
      },
      withDispositiveProvisions,
    },
  };
}

function getAccountIdField(opts: MakeBdgInputOpts): Record<string, string> {
  if (opts.isOnHypotheticalWaterfall) {
    if (opts.entityType === 'retirement-account') {
      return { hypotheticalRetirementPersonalAccountID: opts.entitySubtypeId };
    }
    return { hypotheticalInsurancePersonalAccountID: opts.entitySubtypeId };
  }

  if (opts.entityType === 'retirement-account') {
    return { retirementPersonalAccountID: opts.entitySubtypeId };
  }
  return { insurancePersonalAccountID: opts.entitySubtypeId };
}
